import React, {Component} from "react";
import {connect} from "react-redux";
import {Translate, translate} from "react-i18nify";
import {ReactSVG} from "react-svg";

import * as componentTypes from "../../../../constants/componentTypes";
import * as settings from "../../../../constants/settings";
import * as formulaActions from "../../../../actions/formulaActions";
import * as navigationActions from "../../../../actions/navigationActions";
import * as tooltipHelper from "../../../../helpers/tooltipHelper";
import * as modelHelper from "../../../../helpers/modelHelper";

import FormulaPropertiesComponent from "../tab/mainTab/FormulaProperties";

import imgAccepted from "../../../../resources/img/svgicons/circle-check-solid.svg";
import imgConfirm from "../../../../resources/img/svgicons/confirm.svg";
import imgUnknown from "../../../../resources/img/svgicons/cancel.svg";
import imgVerified from "../../../../resources/img/svgicons/badge-check-solid.svg";
import Modal from "../../../global/generic/Modal";
import Table from "../../../global/generic/Table";
import Button from "../../../global/generic/Button";
import CancelButton from "../../../global/generic/CancelButton";

class AlternativeFormulasModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activeFormula: null
        };
    }

    getActiveFormulaData = () => {
        for (let formulaData of this.props.alternativeFormulas) {
            if (formulaData.formula.formulaCombinationId === this.state.activeFormula) {
                return formulaData;
            }
        }

        return null;
    };

    handleCloseModalClick = () => {
        this.props.dispatchCloseModalAction();
    };

    handleConfirmButtonClick = () => {
        let formulaData = this.getActiveFormulaData();

        if (formulaData !== null) {
            this.props.dispatchUpdateFormulaAction(this.props.tabId, formulaData);
            this.props.dispatchCloseModalAction();
        }
    };

    getStatusClassname = () => {
        return "status-icon width-30 flex padding-right-5 flex react-svg-icon";
    };

    getStatus = (formula) => {
        if (formula.notAvailable) {
            return (
                <React.Fragment>
                    <ReactSVG className={this.getStatusClassname()} src={imgUnknown} alt="disabled" wrapper="span" />;
                    <Translate value="general.notAvailable" />
                </React.Fragment>
            );
        }

        if (formula.verified) {
            return (
                <React.Fragment>
                    <ReactSVG
                        className={this.getStatusClassname() + " verified-icon"}
                        src={imgVerified}
                        alt="verified"
                        wrapper="span"
                    />
                    <Translate value="general.verified" />
                </React.Fragment>
            );
        }

        if (formula.enabled) {
            return (
                <React.Fragment>
                    <ReactSVG
                        className={this.getStatusClassname() + " accepted-icon"}
                        src={imgAccepted}
                        alt="enabled"
                        wrapper="span"
                    />
                    <Translate value="general.accepted" />
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <ReactSVG className={this.getStatusClassname()} src={imgUnknown} alt="disabled" wrapper="span" />;
                <Translate value="machineSettings.disabled" />
            </React.Fragment>
        );
    };

    getColorantsRows = (formula) => {
        let colorants = [];
        let tooltip = [];

        for (let colorant of formula.getColorantListByUnit(this.props.unit)) {
            colorants.push(colorant.displayName);
            tooltip.push(colorant.displayName + " " + colorant.formatAmount() + " " + colorant.formatAmountType());
        }

        return (
            <div className="col-12 row margin-5">
                <span data-text={tooltip.join(", ")} onMouseEnter={tooltipHelper.createTooltip}>
                    {colorants.join(", ")}
                </span>
            </div>
        );
    };

    getFormulaPropertiesData = (formula) => {
        return (
            <FormulaPropertiesComponent
                tabId={this.props.tabId}
                className="height-3vh"
                iconClassName="margin-5"
                colorantsGroupId={formula.colorantsGroupId}
                destinationId={formula.destinationId}
            />
        );
    };

    getAlternativeFormulasRows = () => {
        if (!Array.isArray(this.props.alternativeFormulas)) {
            return [];
        }

        let rows = [];
        let rowData = null;

        for (let formula of this.props.alternativeFormulas) {
            rowData = [
                modelHelper.buildTableCell(
                    formula.formula.formulaCombinationId === this.props.selectedFormulaId ? (
                        <span className="glyphicon glyphicon-arrow-right color-primary font-size-1_25em" />
                    ) : null
                ),
                modelHelper.buildTableCell(formula.base.displayName),
                modelHelper.buildTableCell(
                    <div className="col-12 flex flex-align-center-vertical">{this.getStatus(formula.formula)}</div>
                ),
                modelHelper.buildTableCell(formula.formula.formatLastUpdateDate()),
                modelHelper.buildTableCell(this.getColorantsRows(formula.formula))
            ];

            if (this.props.formulaPropertiesPosition === settings.POSITION_FORMULA_MORE_INFO_PANEL) {
                rowData.push(
                    modelHelper.buildTableCell(
                        <React.Fragment>
                            {this.getFormulaPropertiesData(formula.formula)}
                            {formula.moreInfoText}
                        </React.Fragment>
                    )
                );
            } else if (this.props.formulaPropertiesPosition === settings.POSITION_FORMULA_STATUS_PANEL) {
                rowData.push(modelHelper.buildTableCell(this.getFormulaPropertiesData(formula.formula)));
                rowData.push(modelHelper.buildTableCell(formula.moreInfoText));
            } else {
                rowData.push(modelHelper.buildTableCell(formula.moreInfoText));
            }

            rows.push(
                modelHelper.buildTableRow(
                    rowData,
                    (this.state.activeFormula === formula?.formula?.formulaCombinationId ? "active" : "") +
                        " cursor-pointer",
                    undefined,
                    {
                        onClick: () => this.setState({activeFormula: formula?.formula?.formulaCombinationId}),
                        onDoubleClick: () =>
                            this.setState(
                                {activeFormula: formula?.formula?.formulaCombinationId},
                                this.handleConfirmButtonClick
                            )
                    }
                )
            );
        }

        return rows;
    };

    getTableHeaders = () => {
        const headers = [
            modelHelper.buildTableCell(""),
            modelHelper.buildTableCell(translate("general.base")),
            modelHelper.buildTableCell(translate("general.status")),
            modelHelper.buildTableCell(translate("general.date")),
            modelHelper.buildTableCell(translate("general.colorants"))
        ];

        if (this.props.formulaPropertiesPosition === settings.POSITION_FORMULA_STATUS_PANEL) {
            headers.push(modelHelper.buildTableCell(translate("mainTab.properties")));
        }

        headers.push(modelHelper.buildTableCell(translate("general.note")));

        return headers;
    };

    getTableColumns = () => {
        let colSize = "col-3";

        if (this.props.formulaPropertiesPosition === settings.POSITION_FORMULA_STATUS_PANEL) {
            colSize = "col-2";
        }

        const columns = [
            modelHelper.buildTableColumn("width-30 vertical-center line-height-3_7vh"),
            modelHelper.buildTableColumn("col-2 vertical-center line-height-3_7vh"),
            modelHelper.buildTableColumn(colSize + " vertical-center line-height-3_7vh"),
            modelHelper.buildTableColumn("col-1 vertical-center line-height-3_7vh"),
            modelHelper.buildTableColumn(colSize + " vertical-center line-height-3_7vh")
        ];

        if (this.props.formulaPropertiesPosition === settings.POSITION_FORMULA_STATUS_PANEL) {
            columns.push(modelHelper.buildTableColumn("col-2 vertical-center line-height-3_7vh"));
        }

        columns.push(modelHelper.buildTableColumn("col-3 vertical-center line-height-3_7vh"));

        return columns;
    };

    getContent = () => {
        return modelHelper.buildModalSection(
            <Table
                className="padding-bottom-10"
                tableHeader={modelHelper.buildTableRow(this.getTableHeaders())}
                tableRows={this.getAlternativeFormulasRows()}
                tableColumns={this.getTableColumns(this.getTableColumns())}
            />
        );
    };

    getFooter = () => {
        return modelHelper.buildModalSection(
            <React.Fragment>
                <Button
                    onClick={this.handleConfirmButtonClick}
                    label={translate("general.confirm")}
                    svgBefore={imgConfirm}
                    disabled={this.state.activeFormula === null}
                    tooltipText={this.state.activeFormula === null ? translate("mainTab.noFormulaSelected") : null}
                />

                <CancelButton onClick={this.handleCloseModalClick} />
            </React.Fragment>
        );
    };

    render() {
        return (
            <Modal
                onClose={this.handleCloseModalClick}
                header={modelHelper.buildModalSection(<Translate value="mainTab.alternativeFormulas" />)}
                content={this.getContent()}
                footer={this.getFooter()}
                styles={{minWidth: "80%"}}
            />
        );
    }
}

const mapStoreToProps = (store, ownProps) => {
    if (store.formula[ownProps.tabId] === undefined) {
        return {
            selectedFormulaId: null,
            alternativeFormulas: [],
            unit: store.settings.formulaUnits,
            formulaPropertiesPosition: store.settings.formulaPropertiesPosition
        };
    }

    return {
        selectedFormulaId: store.formula[ownProps.tabId].formula.formulaCombinationId,
        alternativeFormulas:
            store.formula[ownProps.tabId].alternative === null ? [] : store.formula[ownProps.tabId].alternative,
        unit: store.settings.formulaUnits,
        formulaPropertiesPosition: store.settings.formulaPropertiesPosition
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchCloseModalAction: () => {
            dispatch(navigationActions.closeModal(componentTypes.ALTERNATIVE_FORMULAS_MODAL));
        },
        dispatchUpdateFormulaAction: (tabId, formulaData) => {
            dispatch(formulaActions.updateFormula(tabId, formulaData));
        }
    };
};

export default AlternativeFormulasModal = connect(mapStoreToProps, mapDispatchToProps)(AlternativeFormulasModal);
